import type { PageNumber, PlotMedia } from "@pdfking/renderer-api"
import type { FileName } from "@pdfking/structs"
import { Items, Keys } from "@pdfking/tfunc"

import type { DestinationID, ImposeResult } from "#public"

import { BuildPrintLayers } from "./BuildPrintLayers"

export type MediaSlot = Pick<PlotMedia, "filename" | "page_number" | "print_layer">

/**
 * Where an absolute page goes: once per destination drawn that prints it,
 * named after the destination and numbered by the page's place in it — which
 * file fills that place is the plotting context's business, never the sheet's.
 */
export const MediaSlots = (
  destinations: ImposeResult["destinations"]
): ((drawn: DestinationID[]) => (page: PageNumber) => MediaSlot[]) => {
  const print_layers = BuildPrintLayers(Keys(destinations) as DestinationID[])
  const slots = new Map<PageNumber, MediaSlot[]>()
  for (const [id, pages] of Items(destinations))
    pages.forEach((page, i) =>
      slots.set(page, [
        ...(slots.get(page) ?? []),
        {
          filename: id as string as FileName,
          page_number: (i + 1) as PageNumber,
          print_layer: print_layers[id as DestinationID]!,
        },
      ])
    )
  return drawn => {
    const keep = new Set<string>(drawn)
    return page => (slots.get(page) ?? []).filter(slot => keep.has(slot.filename))
  }
}
